import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Avatar, Button, Typography, Upload, Checkbox, Divider, Collapse, Input } from 'antd';
import { TierProgressList } from '@/components/common/organisms';
import { BREAKPOINTS, Container, Flex, Col } from '@/ui';
import { Text } from '@/ui/typography';
import { DeepCheckModule } from '@/modules';
import { COLORS } from '@/utils/static';
import { useTranslation } from 'react-i18next';
import { EditOutlined, LoadingOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons';
import { UpdateNameModal, UpdateProfileTypeModal } from '@/components/admin';
import { adminApi, adminReferralApi, adminReferralApiKeys, investorApi } from '@/api';
import { DISPLAY_INVESTOR_ACCOUNT_STATUS } from '@/utils/static';
import { dateString } from '@/utils/helpers';
import { usePopups, useWindowSize } from '@/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { investorActions } from '@/store/investor';
import { isSmallScreen } from '@/components/common/atoms/Table/utils';

const CollapseAdditionalInfo = ({ investor, label, firstIndex, lastIndex }) => {
  const { t } = useTranslation();

  return (
    <Collapse
      items={[
        {
          key: label,
          label: t(`${label}`),
          children: (
            <DropdownMenu>
              {investor.answers.map((item) => (
                <>
                  <Text $color={COLORS.greyText2} $fontSize={14} $maxWidth={150}>
                    {t(`${item.title}`)}
                  </Text>
                  <Text $maxWidth={150}>{item.answer}</Text>
                </>
              ))}
            </DropdownMenu>
          )
        }
      ]}
    />
  );
};

const getBase64 = (img, callback) => {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result));
  reader.readAsDataURL(img);
};

export const ProfileInfoView = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showErrorToast, showSuccessToast } = usePopups();
  const queryClient = useQueryClient();
  const { width: windowWidth } = useWindowSize();
  const smallScreen = isSmallScreen(windowWidth);
  const investor = useSelector((state) => state.investor);
  const { userRole } = useSelector((state) => state.user);
  const [code, setCode] = useState(null);
  const [codeOpened, setCodeOpened] = useState(false);
  const [accountRole, setAccountRole] = useState(null);
  const [isOpenChangeNameModal, setIsOpenChangeNameModal] = useState(false);
  const [changeProfileTypeOpened, setChangeProfileTypeOpened] = useState(false);
  const [changeAvatarMode, setChangeAvatarMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState();
  const [imgSrc, setImgSrc] = useState(null);

  const handleChange = (info) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }
    if (info.file.status === 'done') {
      getBase64(info.file.originFileObj, (url) => {
        setLoading(false);
        setImageUrl(url);
      });
    }
  };

  const refetchInvestorInfo = async () => {
    await queryClient.refetchQueries({ queryKey: ['fetchInvestorProfile'] });
  };

  const uploadButton = (
    <button
      style={{ border: 0, background: 'none', width: '160px', height: '160px' }}
      type='button'
    >
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  useEffect(() => {
    if (investor && investor.avatar) {
      const fetchImage = async () => {
        try {
          const response = await adminApi.getAvatar(investor.avatar);
          setImgSrc(URL.createObjectURL(response.data));
        } catch (error) {
          console.error('Error fetching image:', error);
          showErrorToast(error, true);
        }
      };

      fetchImage();
    }
  }, [investor]);

  const lowerCaseFirstWordUpperCase = (text) => {
    if (typeof text !== 'string' || text.length === 0) {
      return '';
    }

    const lowerCaseText = text.toLowerCase();

    const words = lowerCaseText.split(' ');

    if (words.length > 0) {
      const firstWord = words[0];
      const capitalizedFirstWord = firstWord.charAt(0).toUpperCase() + firstWord.slice(1);
      words[0] = capitalizedFirstWord;
    }

    const result = words.join(' ');
    return result;
  };

  useEffect(() => {
    if (investor && investor.accountRole) {
      setAccountRole(investor.accountRole);
    }
  }, [investor]);

  const { data: codeRequest } = useQuery({
    queryKey: [adminReferralApiKeys.getInvestorRequest, +id, accountRole],
    queryFn: () =>
      adminReferralApi.getInvestorRequest({
        investorId: +id,
        role: accountRole,
        requestType: 'REFERRAL_CODE'
      }),
    select: (data1) => data1.data,
    enabled: Boolean(accountRole) && accountRole !== 'INVESTOR'
  });

  const updateReferralCode = async () => {
    try {
      await adminReferralApi.setRefferalCode({
        investorId: +id,
        role: investor.accountRole,
        code: code.trim()
      });
      if (codeRequest && codeRequest.id) {
        await adminReferralApi.resolveRequest(codeRequest.id);
      }

      dispatch(investorActions.setInvestorParams({ referralCode: code.trim() }));
      setCode('');
      setCodeOpened(false);
      showSuccessToast('Code successfully changed');
      await queryClient.refetchQueries([adminReferralApiKeys.getInvestorRequest, +id]);
    } catch (e) {
      showErrorToast(e, true);
    }
  };

  if (investor) {
    const {
      id,
      firstName,
      lastName,
      citizenship,
      phone,
      email,
      tier,
      accountStatus,
      createdAt,
      lastSession,
      scoringPoints,
      referralCode,
      isAgent,
      isRecommender,
      isAccessibleWithoutVerification
    } = investor;

    const changeQuestionnaireAccess = async (e) => {
      const { checked } = e.target;

      try {
        const result = await adminApi.updateQuestionnaireAccess({
          investorId: id,
          isAccessibleWithoutVerification: checked
        });

        if (result) {
          await refetchInvestorInfo();
          showSuccessToast('Questionnaire access is changed');
        }
      } catch (e) {
        console.error(e);
        showErrorToast(e, true);
      }
    };

    return (
      <>
        <Container $paddingY={25} $marginBottom={40}>
          <Flex align={'center'} justify={'space-between'} wrap>
            <Typography.Title level={2}>{t('Personal data')}</Typography.Title>
          </Flex>

          <Flex
            align={smallScreen || userRole === 'admin' ? 'flex-start' : 'flex-end'}
            justify={'space-between'}
            $marginTop={36}
            vertical={smallScreen}
            gap={24}
          >
            <StyledInfoRow gap={24}>
              <Flex vertical gap={10}>
                {changeAvatarMode ? (
                  <Upload
                    name='avatar'
                    listType='picture-card'
                    className='avatar-uploader'
                    showUploadList={false}
                    customRequest={(options) =>
                      userRole === 'admin'
                        ? adminApi.updateInvestorAvatar(id, options.file).then(() => {
                            setLoading(false);
                            setChangeAvatarMode(false);
                            refetchInvestorInfo();
                          })
                        : investorApi.uploadAvatar(options.file).then(() => {
                            setLoading(false);
                            setChangeAvatarMode(false);
                            refetchInvestorInfo();
                          })
                    }
                    onChange={handleChange}
                    style={{
                      width: '160px',
                      height: '160px'
                    }}
                  >
                    {imageUrl ? (
                      <img src={imageUrl} alt='avatar' style={{ width: '100%' }} />
                    ) : (
                      uploadButton
                    )}
                  </Upload>
                ) : (
                  <Avatar shape='square' size={160} src={imgSrc} icon={<UserOutlined />} />
                )}

                <Button onClick={() => setChangeAvatarMode(!changeAvatarMode)}>
                  {t('Change avatar')}
                </Button>
              </Flex>

              <Flex vertical justify='space-between' gap={24}>
                <Flex gap={8}>
                  <StyledTitle level={3}>
                    {firstName} {lastName}
                  </StyledTitle>

                  {userRole === 'admin' && (
                    <>
                      <div style={{ marginLeft: '10px' }}>
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => setIsOpenChangeNameModal(true)}
                        />
                      </div>
                      <UpdateNameModal
                        open={isOpenChangeNameModal}
                        onClose={() => setIsOpenChangeNameModal(false)}
                        initialFirstName={firstName}
                        initialLastName={lastName}
                        refetchInvestorInfo={refetchInvestorInfo}
                      />
                    </>
                  )}
                </Flex>

                <BlockContacts>
                  <Text $color={COLORS.greyText2} $fontSize={14}>
                    {t('Phone number')}
                  </Text>

                  <Text>{phone}</Text>

                  <Text $color={COLORS.greyText2} $fontSize={14}>
                    {t('E-mail')}
                  </Text>

                  <Text $fontSize={14}>{email}</Text>

                  <Text $color={COLORS.greyText2} $fontSize={14}>
                    {t('Citizenship')}
                  </Text>

                  <Text $fontSize={14}>{citizenship?.country}</Text>

                  <Text $color={COLORS.greyText2} $fontSize={14}>
                    {t('Profile type')}
                  </Text>

                  {userRole === 'investor' && (
                    <Text $fontSize={14}>
                      {isAgent ? t('Agent') : isRecommender ? t('Assistant') : t('Investor')}
                    </Text>
                  )}

                  {userRole === 'investor' && referralCode && (
                    <>
                      <Text $color={COLORS.greyText2} $fontSize={14}>
                        {t('Referral code')}
                      </Text>

                      <Typography.Text copyable style={{ fontSize: 14 }}>
                        {referralCode}
                      </Typography.Text>
                    </>
                  )}
                </BlockContacts>
              </Flex>
            </StyledInfoRow>

            {userRole === 'admin' && (
              <Col
                $paddingY={15}
                $width={'44%'}
                style={{
                  borderLeft:
                    window.innerWidth > BREAKPOINTS.md ? `1px solid ${COLORS.greyBasic}` : '',
                  paddingLeft: window.innerWidth > BREAKPOINTS.md ? '20px' : ''
                }}
              >
                <div>
                  {t('Status')}:{' '}
                  <strong>{t(DISPLAY_INVESTOR_ACCOUNT_STATUS[accountStatus])}</strong>
                </div>

                <Divider size='middle' />

                <div>
                  {accountRole && (
                    <Col span={6}>
                      <Flex gap={4}>
                        <span>{t('Code')}:</span>
                        {codeRequest ? (
                          <span style={{ color: COLORS.orange }}>{t('Requested')}</span>
                        ) : (
                          <strong>{referralCode ?? t('none')}</strong>
                        )}
                      </Flex>
                      <Flex $marginTop={10} $marginBottom={5}>
                        {!codeOpened && (
                          <Button onClick={() => setCodeOpened(true)} type={'primary'}>
                            {referralCode ? t('Change code') : t('Set code')}
                          </Button>
                        )}
                      </Flex>
                      {codeOpened && (
                        <Container $marginTop={10}>
                          <Input
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder={t('Enter code')}
                          />
                          <Flex gutter={8} style={{ marginTop: '16px' }}>
                            <Col>
                              <Button onClick={() => setCodeOpened(false)}>{t('Cancel')}</Button>
                            </Col>
                            <Col>
                              <Button
                                onClick={updateReferralCode}
                                disabled={!code}
                                type={'primary'}
                              >
                                {t('Save')}
                              </Button>
                            </Col>
                          </Flex>
                        </Container>
                      )}
                    </Col>
                  )}
                </div>
                <Divider size='middle' />
                <div>
                  {t('Registered')}:{' '}
                  <strong>{createdAt ? dateString(new Date(createdAt)) : '-'}</strong>
                </div>
                <div>
                  {t('Last session')}:{' '}
                  <strong>{lastSession ? dateString(new Date(lastSession)) : '-'}</strong>
                </div>
                <div>
                  {t('Tier level')}:{' '}
                  <strong>
                    {t('Tier')} {tier}
                  </strong>
                </div>
                <div>
                  {t('Scoring points')}: <strong>{scoringPoints}</strong>
                </div>

                <Divider size='middle' />

                <div>
                  {t('Legal entity connections')}: {t('none')}
                </div>
              </Col>
            )}

            {userRole !== 'admin' && (
              <Container $maxWidth={379} $wide>
                <TierProgressList tier={tier} />
              </Container>
            )}
          </Flex>

          {userRole === 'admin' && (
            <Container $marginTop={20}>
              <Divider />
              <Flex gap={20} align={'center'} justify='space-between'>
                <Text $color={COLORS.greyText2} $fontSize={14}>
                  {t('Profile type')}
                </Text>

                <Flex gap={8} align={'center'}>
                  <Text $fontSize={14}>
                    {isAgent ? t('Agent') : isRecommender ? t('Assistant') : t('Investor')}
                  </Text>

                  {userRole === 'admin' && (
                    <Button onClick={() => setChangeProfileTypeOpened(true)}>{t('Change')}</Button>
                  )}
                </Flex>
              </Flex>
              <Flex $marginY={18}>
                <Checkbox
                  name='isAccessibleWithoutVerification'
                  checked={isAccessibleWithoutVerification}
                  onChange={changeQuestionnaireAccess}
                >
                  {t('Access to questionnaires without verification approve')}
                </Checkbox>
              </Flex>
            </Container>
          )}

          <Container $marginTop={20}>
            {(userRole === 'admin' || userRole === 'superAdmin') && (
              <>
                <Divider />
                <DeepCheckModule investorId={id} viewMode={userRole} />
              </>
            )}
          </Container>

          {investor.additionalInfo && (
            <Container $marginTop={40}>
              <Text $fontSize={20} $fontWeight={600} $color={COLORS.greyText2}>
                {t('Additional information')}
              </Text>

              <BlockAdditionalInfo $marginTop={32}>
                {investor.additionalInfo.answers.map((item, index) => (
                  <CollapseAdditionalInfo
                    key={index}
                    label={lowerCaseFirstWordUpperCase(item.sectionName)}
                    investor={item}
                  />
                ))}
              </BlockAdditionalInfo>
            </Container>
          )}
          <UpdateProfileTypeModal
            open={changeProfileTypeOpened}
            onClose={() => setChangeProfileTypeOpened(false)}
            isAgent={investor.isAgent}
            isRecommender={investor.isRecommender}
            refetchInvestorInfo={refetchInvestorInfo}
          />
        </Container>
      </>
    );
  }

  return null;
};

const StyledTitle = styled(Typography.Title)`
  font-weight: 600 !important;
`;

const BlockContacts = styled.div`
  padding-bottom: 5px;
  display: grid;
  flex-direction: row;
  grid-template-columns: max-content max-content;
  column-gap: 20px;
  row-gap: 18px;
  align-items: center;

  @media (max-width: ${BREAKPOINTS.md}px) {
    grid-template-columns: 150px 150px;
    gap: 10px;
  }
`;

const BlockAdditionalInfo = styled(Container)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
  justify-content: space-between;
  row-gap: 32px;
  @media (max-width: ${BREAKPOINTS.md}px) {
    grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
  }
`;

const DropdownMenu = styled.div`
  display: grid;
  flex-direction: row;
  grid-template-columns: max-content max-content;
  column-gap: 12px;
  row-gap: 14px;
  margin-top: 16px;
  visibility: visible;
  height: auto;
  max-height: max-content;
`;

const StyledInfoRow = styled(Flex)`
  @media (max-width: ${BREAKPOINTS.md}px) {
    flex-direction: column;
  }
`;
